import type { GetServerSideProps, GetServerSidePropsContext, NextPage } from "next";
import { getWhatsOnPage } from "../../api/graphql/queries";
import WhatsOnListing from "../../cmsPages/WhatsOnListing";
import { initializeApollo } from "../../lib/apolloClient";
import { getFrontendHostname, getTheme } from '../../lib/config';
import { WhatsonListingFragment } from './../../__generated__/graphql';
import Error404 from "./../../errorPages/Error404";
import Error500 from "./../../errorPages/Error500";


// Events listing page
type WhatsOnPageProps = {
  data: {
    whatson: WhatsonListingFragment
  };
  pageUrl: string;
  siteName: string;
  homepageUrl: string;
  errorCode?: number;
};

const WhatsOnPage: NextPage<WhatsOnPageProps> = (props: WhatsOnPageProps) => {

  if (props.errorCode === 500) {
    return <Error500 />;
  }
  else if (props.errorCode) {
    return <Error404 />;
  }
  return <WhatsOnListing pageUrl={props.pageUrl} {...props.data.whatson} />;
};

export default WhatsOnPage;

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
  const { res, req } = context;
  const theme = getTheme();
  const hostname = getFrontendHostname();

  let pageUrl;
  if (req) {
    // Server side rendering
    pageUrl = 'https://' + hostname + req.url;
  } else {
    // Client side rendering
    pageUrl = window.location.href;
  }
  const url = new URL(pageUrl);
  // ZSL and Zoos use different URLs to reach this page. Only allow the correct URL so we don't create duplicate content.
  if ((theme === 'zsl' && url.pathname.includes('zoo-stories'))
    || (theme !== 'zsl' && url.pathname.includes('news-and-events'))) {
    res.statusCode = 404;
    return {
      props: {
        theme: theme,
        pageUrl: pageUrl,
        hostname: hostname,
        errorCode: 404
      }
    };
  }

  // Get current params for the query.
  const params = url.searchParams;
  // Page number
  const page = (Number.parseInt(params.get('page') || '0|') - 1) || 0;
  // Tag filters.
  const tags = params.getAll('tags');
  const date = (theme === 'zsl') ? null : params.get('date');
  const flatTags = tags ? tags.map((value) => value.split(',')).flat() : null;

  const apolloClient = initializeApollo();
  const result = await apolloClient.query({
    query: getWhatsOnPage,
    variables: {
      page: Math.max(page, 0),
      filterTags: flatTags || null,
      eventDateFrom: date
    }
  })
  .catch((e) => {
    console.error('[GraphQL] Events query failed:', e);
    res.statusCode = 500;
    return {
      data: {
        route:{}
      },
      errorCode: 500
    };
  });

  return {
    props: {
      theme: theme,
      pageUrl: pageUrl,
      hostname: hostname,
      ...result
    },
  };
};
